/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { PageQueryParams, SearchQueryParams } from '../apiCommon.typings';
import { CashBoxesStatus } from '../cashBoxes/cashBoxesClient.typings';
import { companiesStatus } from '../companies/companiesClient.typings';
import { storeStatus, StoreType } from '../stores/storesClientTypings';

export enum DeviceStatus {
    PRODUCED = 'PRODUCED',
    READY = 'READY',
    STOCK = 'STOCK',
    STORE = 'STORE',
    RECYCLED = 'RECYCLED',
    BROKEN = 'BROKEN',
    SERVICE = 'SERVICE',
    FIXED = 'FIXED',
    LOST = 'LOST',
}

export enum DeviceUSBType {
    A = 'A',
    microB = 'microB',
}

export enum DeviceWorkability {
    NEW = 'NEW',
    JUNIOR = 'JUNIOR',
    SENIOR = 'SENIOR',
    VETERAN = 'VETERAN',
    INVALID = 'INVALID',
}

export interface DevicesPageQueryParams {
    afterKeyFactoryNumber: string;
    limit?: number;
}

export interface DevicesListFilterParams extends Partial<SearchQueryParams> {
    deviceStatuses?: DeviceStatus[];
    workabilities?: DeviceWorkability[];
    usbTypes?: DeviceUSBType;
    firmwareVersions?: string[];
    operatorNames?: string[];
}

export type DevicesListRequestParams = DevicesListFilterParams & DevicesPageQueryParams;
export type DevicesListExcelRequestParams = DevicesListFilterParams;

export interface DevicesListDto {
    afterKeyFactoryNumber: string;
    devices: DeviceDto[];
}

export interface DeviceDto {
    factoryNumber: string;
    status: DeviceStatus;
    statusDate: string;
    statusComment: string;
    usbType: DeviceUSBType;
    firmwareVersion: string;
    wearout: number;
    age: number;
    experience: number;
    workability: DeviceWorkability;
    operatorName?: string;
    operatorTaxNumber?: string;
    cashBoxTaxNumber?: string;
    cashBoxStatus?: CashBoxesStatus;
    temp?: boolean;
    cashRegisterFactoryNumber?: string;
    companyTaxNumber?: string;
    storeTaxNumber?: string;
}

export interface DeviceDetailsDto {
    factoryNumber: string;
    usbType: DeviceUSBType;
    status: DeviceStatus;
    statusDate: number;
    statusComment: string;
    firmwareVersion: string;
    wearout: number;
    age: number;
    experience: number;
    workability: DeviceWorkability;
    operatorTaxNumber: string;
    operatorName: string;
    cashBoxTaxNumber: string;
    temporary: boolean;
    cashBoxStatus: CashBoxesStatus;
    cashRegisterFactoryNumber: string;
    cashRegisterModelName: string;
    cashRegisterModelTaxNumber: string;
    storeTaxNumber: string;
    storeName: string;
    fullAddress: string;
    storeType: StoreType;
    storeStatus: storeStatus;
    companyTaxNumber: string;
    companyName: string;
    companyStatus: companiesStatus;
    productionId: string;
    productionPlace: string;
    productionDate: string;
    hardwareVersion: string;
}

// -----Firmware versions history----- //

export type FirmwareHistoryQueryParams = PageQueryParams;

export interface FirmwareHistoryItemDto {
    firmwareVersion: string;
    insertedDateAtUtc: string;
    duration: number;
}

export interface FirmwareHistoryListDto {
    count: number;
    items: FirmwareHistoryItemDto[];
}

// -----Status history----- //

export type StatusHistoryQueryParams = PageQueryParams;

export interface StatusHistoryItemDto {
    status: DeviceStatus;
    insertedDateAtUtc: string;
    duration: number;
}

export interface StatusHistoryListDto {
    count: number;
    items: StatusHistoryItemDto[];
}

// -----Operator history----- //

export type OperatorHistoryQueryParams = PageQueryParams;

export interface OperatorHistoryItemDto {
    operatorName: string;
    operatorTaxNumber: string;
    insertedDateAtUtc: string;
    duration: number;
}

export interface OperatorHistoryListDto {
    count: number;
    items: OperatorHistoryItemDto[];
}

// -----Registration history----- //

export interface RegistrationHistoryItemDto {
    taxNumber: string;
    insertedDate: string;
    eliminatedDate: string;
    duration: number;
}
